import { useCallback, useEffect, useState } from "react";
import { Brain, ChevronDown, ChevronRight } from "lucide-react";
import { api } from "../../lib/api";

const pc = (v) => (v == null ? "—" : `${v}%`);
const tone = (v, good = true) => (v == null ? "text-[var(--r2-text2)]" : (good ? v >= 50 : v < 30) ? "text-[var(--r2-emerald)]" : "text-[var(--r2-amber)]");

/* HIVE MEMORY — every council case, what happened 1h/6h/24h later, how each agent is calibrated, and the plain-vs-memory council A/B (report only). */
export const HiveMemory = ({ mem }) => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const load = useCallback(() => api.meme.hiveMemory(30).then(setData).catch(() => {}), []);
  useEffect(() => { if (open) { load(); const t = setInterval(load, 60000); return () => clearInterval(t); } }, [open, load]);
  const s = data?.summary, r = data?.report, ab = r?.ab;
  return (
    <div className="rounded-sm border border-[var(--r2-border)] bg-[color-mix(in_srgb,var(--r2-panel)_80%,transparent)] px-3 py-1.5 text-[9px] font-mono" data-testid="hive-memory">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center gap-3 text-left" data-testid="hive-memory-toggle">
        {open ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
        <span className="flex items-center gap-1.5 font-black tracking-[0.18em] text-[var(--r2-cyan)]"><Brain size={11} /> HIVE MEMORY</span>
        <span className="text-[var(--r2-text2)]" data-testid="hive-memory-summary">
          {mem?.enabled === false ? "OFF" : <>
            <span className="text-[var(--r2-text1)]">{s?.cases ?? mem?.recorded ?? 0}</span> cases · <span className="text-[var(--r2-text1)]">{s?.labelled ?? 0}</span> with outcomes ·{" "}
            <span className="text-[var(--r2-text1)]">{s?.shadow_runs ?? mem?.shadow_runs ?? 0}</span> shadow A/B runs
            {s?.base_rates?.n ? <> · base rate {s.base_rates.RUNNER ?? 0}% RUNNER / {s.base_rates.DEAD ?? 0}% DEAD</> : null}
          </>}
        </span>
        <span className="ml-auto text-[var(--r2-text2)]">advisory — weights never auto-changed</span>
      </button>
      {open && r && (
        <div className="mt-1.5 space-y-1.5" data-testid="hive-memory-body">
          <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 md:grid-cols-5">
            {Object.entries(r.agents || {}).map(([name, a]) => (
              <div key={name} className="text-[var(--r2-text2)]" data-testid={`hive-memory-agent-${name}`}>
                <div className="text-[var(--r2-text1)] font-bold">{name.replace("_agent", "").toUpperCase()} <span className="font-normal text-[var(--r2-text2)]">n={a.n}</span></div>
                <div>BUY precision <span className={tone(a.buy_precision)}>{pc(a.buy_precision)}</span> · FP <span className={tone(a.buy_false_positive, false)}>{pc(a.buy_false_positive)}</span></div>
                <div>AVOID missed runners <span className={tone(a.avoid_missed_runner, false)}>{pc(a.avoid_missed_runner)}</span> · Brier {a.brier ?? "—"}</div>
                <div>calib &lt;50 {pc(a.calibration?.["<50"])} · 50-75 {pc(a.calibration?.["50-75"])} · 75+ {pc(a.calibration?.["75+"])}</div>
              </div>
            ))}
            {!Object.keys(r.agents || {}).length && <span className="text-[var(--r2-text2)] col-span-full" data-testid="hive-memory-empty">no labelled cases yet — outcomes are marked 1h / 6h / 24h after each council convene</span>}
          </div>
          {ab && (
            <div className="flex flex-wrap gap-x-6 text-[var(--r2-text2)]" data-testid="hive-memory-ab">
              {["council", "memory_council"].map((k) => (
                <span key={k}><span className="text-[var(--r2-text1)]">{k === "council" ? "COUNCIL" : "MEMORY COUNCIL"}</span> n={ab[k]?.n ?? 0} · ENTER {ab[k]?.enter ?? 0} · precision <span className={tone(ab[k]?.precision)}>{pc(ab[k]?.precision)}</span> · FP <span className={tone(ab[k]?.false_positive, false)}>{pc(ab[k]?.false_positive)}</span> · missed runners {ab[k]?.missed_runners ?? 0} · {ab[k]?.latency_ms ? `${ab[k].latency_ms}ms` : "—"}</span>
              ))}
              {r.premature_exits > 0 && <span>premature exits {r.premature_exits}</span>}
            </div>
          )}
          {(r.signals?.best || []).length > 0 && (
            <div className="text-[var(--r2-text2)]" data-testid="hive-memory-signals">
              best signals: {r.signals.best.slice(0, 4).map((x) => `"${x.signal}" ${x.hit_rate}% (n=${x.n})`).join(" · ")}
            </div>
          )}
          <ul className="space-y-0.5" data-testid="hive-memory-recs">
            {(r.recommendations || []).map((x, i) => <li key={i} className="text-[var(--r2-amber)]">→ {x}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
};
